import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PrintDocumentButton, RecordDocuments } from '../components/PrintDocument';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, type StocktakeApplyResult, type StocktakeSession } from '../api';
import { useAuth } from '../auth';
import { stocktakeFinishState, stocktakePrintRows } from '../lib/stocktakeForm';
import { useColors, useStocktakeSession } from '../queries';
import {
  Badge, Button, Card, Empty, ErrorMessage, Field, Input, Notice, PageHeader, Select, Spinner, number, plural, useConfirm, useLeaveGuard,
} from '../ui';

type Entry = {
  id?: number;
  itemType: string;
  itemNo: string;
  itemName: string;
  colorId: number;
  colorName: string;
  colorRgb: string | null;
  condition: string;
  subCondition: string | null;
  expectedQty: number;
  counted: string;
  note: string;
};

const toEntry = (r: StocktakeSession['rows'][number]): Entry => ({
  id: r.id,
  itemType: r.itemType,
  itemNo: r.itemNo,
  itemName: r.itemName,
  colorId: r.colorId,
  colorName: r.colorName,
  colorRgb: r.colorRgb,
  condition: r.condition,
  subCondition: r.subCondition,
  expectedQty: r.expectedQty,
  counted: r.countedQty === null ? '' : String(r.countedQty),
  note: r.note ?? '',
});

export function StocktakeDetail() {
  const { id } = useParams();
  const sessionId = Number(id);
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { confirm, dialog } = useConfirm();
  const colors = useColors();
  const session = useStocktakeSession(sessionId);

  const [entries, setEntries] = useState<Entry[]>([]);
  const [note, setNote] = useState('');
  const [loadedRev, setLoadedRev] = useState(0);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [found, setFound] = useState({ itemType: 'PART', itemNo: '', colorId: 0, condition: 'U', subCondition: '' });

  const loadedSession = useRef<number | null>(null);
  useEffect(() => {
    if (!session.data) return;
    // Background reads must not replace local observations or the revision they were based on.
    if (loadedSession.current === session.data.id && session.data.state === 'counting') return;
    loadedSession.current = session.data.id;
    setEntries(session.data.rows.map(toEntry));
    setNote(session.data.note ?? '');
    setLoadedRev(session.data.draftRevision);
    setDirty(false);
  }, [session.data]);

  const { dialog: leaveDialog, release } = useLeaveGuard(dirty);

  const countedCount = entries.filter((e) => e.counted !== '').length;
  const total = entries.length;

  const differences = useMemo(() => {
    const out = { additions: [] as Entry[], shortages: [] as Entry[], unchanged: 0, uncounted: [] as Entry[] };
    for (const e of entries) {
      if (e.counted === '') out.uncounted.push(e);
      else {
        const diff = Number(e.counted) - e.expectedQty;
        if (diff === 0) out.unchanged++;
        else if (diff > 0) out.additions.push(e);
        else out.shortages.push(e);
      }
    }
    return out;
  }, [entries]);

  const missingNotes = entries.filter((e) => e.counted !== '' && Number(e.counted) !== e.expectedQty && !e.note.trim());

  const save = useMutation({
    mutationFn: () =>
      api<{ session: StocktakeSession }>(`/api/v1/stocktaking/${sessionId}`, {
        method: 'PATCH',
        body: {
          draftRevision: loadedRev,
          note: note.trim() ? note.trim() : null,
          rows: entries.map((e) => ({
            ...(e.id !== undefined ? { id: e.id } : {}),
            itemType: e.itemType,
            itemNo: e.itemNo,
            colorId: e.colorId,
            condition: e.condition,
            subCondition: e.subCondition,
            countedQty: e.counted === '' ? null : Number(e.counted),
            note: e.note.trim() ? e.note.trim() : null,
          })),
        },
      }).then((r) => r.session),
    onSuccess: (s) => {
      setEntries(s.rows.map(toEntry));
      setNote(s.note ?? '');
      setLoadedRev(s.draftRevision);
      setDirty(false);
      setSavedAt(new Date().toISOString());
    },
  });

  const apply = useMutation({
    mutationFn: (scope: 'full' | 'partial') =>
      api<{ result: { idempotent: boolean; result: StocktakeApplyResult; session: StocktakeSession } }>(`/api/v1/stocktaking/${sessionId}/apply`, {
        body: { scope, draftRevision: loadedRev },
      }).then((r) => r.result),
    onSuccess: ({ result }) => {
      release();
      void qc.invalidateQueries({ queryKey: ['stocktake'] });
      void qc.invalidateQueries({ queryKey: ['stocktakes'] });
      void qc.invalidateQueries({ queryKey: ['locations'] });
      void qc.invalidateQueries({ queryKey: ['lots'] });
      setEntries([]);
      setDirty(false);
      setSavedAt(null);
      // Show the result in-place on the history view (the next fetch reflects the applied state).
      void qc.invalidateQueries({ queryKey: ['stocktake', sessionId] });
      navigate(`/stocktaking/${result.sessionId}`);
    },
  });

  const cancel = useMutation({
    mutationFn: () => api(`/api/v1/stocktaking/${sessionId}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      release();
      void qc.invalidateQueries({ queryKey: ['stocktake', sessionId] });
      void qc.invalidateQueries({ queryKey: ['locations'] });
      navigate('/stocktaking');
    },
  });

  const owner = user?.role === 'owner';
  const writable = user?.role === 'owner' || user?.role === 'staff';

  if (session.isPending) return <Spinner />;
  if (session.error) return <ErrorMessage error={session.error} />;
  const s = session.data;

  const finished = s.state !== 'counting';

  async function finish(scope: 'full' | 'partial') {
    if (scope === 'full' ? !finishState.full : !finishState.partial) return;
    const label = s.capturedLabel;
    const addPieces = differences.additions.reduce((sum, e) => sum + (Number(e.counted) - e.expectedQty), 0);
    const remPieces = differences.shortages.reduce((sum, e) => sum + (e.expectedQty - Number(e.counted)), 0);
    const ok = await confirm(
      `Apply this ${scope} count of ${label}? ${addPieces} piece(s) added, ${remPieces} removed. Corrections affect BrickLoop immediately; BrickLink needs a separate Sync review.`,
      { label: 'Apply count', danger: true },
    );
    if (ok) apply.mutate(scope);
  }

  function addFound() {
    if (!found.itemNo.trim()) return;
    setEntries((prev) => [
      ...prev,
      {
        itemType: found.itemType,
        itemNo: found.itemNo.trim(),
        itemName: '',
        colorId: found.colorId,
        colorName: colors.data?.find((c) => c.id === found.colorId)?.name ?? '',
        colorRgb: colors.data?.find((c) => c.id === found.colorId)?.rgb ?? null,
        condition: found.condition,
        subCondition: found.subCondition || null,
        expectedQty: 0,
        counted: '',
        note: '',
      },
    ]);
    setFound({ ...found, itemNo: '' });
    setDirty(true);
  }

  function patch(i: number, patch: Partial<Entry>) {
    setEntries((prev) => prev.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
    setDirty(true);
  }

  const finishState = stocktakeFinishState(entries, { owner, dirty, stale: s.stale, busy: save.isPending || apply.isPending || cancel.isPending });

  return (
    <>
      {dialog}
      {leaveDialog}
      <PageHeader
        title={`Count ${s.capturedLabel}`}
        subtitle={finished ? 'This count is finished.' : 'Count each row. Blank means not counted; zero means checked and none found.'}
        actions={
          writable && !finished ? (
            <>
              <Button variant="secondary" onClick={() => navigate('/stocktaking')}>
                Back
              </Button>
              <Button variant="secondary" onClick={() => window.open(`/stocktaking/${sessionId}/sheet`, '_blank')}>Print count sheet</Button>
              <Button variant="secondary" disabled={save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
              <Button
                variant="secondary"
                disabled={save.isPending}
                onClick={async () => {
                  try {
                    await save.mutateAsync();
                    release();
                    navigate('/stocktaking');
                  } catch { /* The mutation error stays visible; retain the unsaved draft. */ }
                }}
              >
                Save and leave
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={() => navigate('/stocktaking')}>
              Back
            </Button>
          )
        }
      />
      <div className="space-y-4">
        <RecordDocuments recordType="stocktake" recordId={sessionId} />
        {s.stale && !finished && (
          <Notice tone="amber">This bin changed after counting started. Recount it before applying.</Notice>
        )}
        {!finished && (
          <Notice>Keep this bin unchanged while counting. If its stock changes in BrickLoop, you will need to recount it before applying.</Notice>
        )}
        {dirty ? <Notice tone="amber">Unsaved changes. Save before finishing this count.</Notice> : savedAt && <Notice tone="green">Saved.</Notice>}
        <ErrorMessage error={save.error ?? apply.error ?? cancel.error} />

        {finished ? (
          <Card title="Result">
            <p className="text-sm text-slate-600">
              {s.state === 'cancelled' ? 'Cancelled count. No stock corrections applied.' : <>{s.scope === 'partial' ? 'Partial' : 'Full'} count applied by{' '}
              <span className="font-medium">user {s.appliedById}</span> on {s.appliedAt ? new Date(s.appliedAt).toLocaleString('en-GB') : '—'}.
              {s.result ? ` ${plural(s.result.piecesAdded, 'piece')} added, ${plural(s.result.piecesRemoved, 'piece')} removed.` : ''}</>}
            </p>
            <div className="mt-3 grid gap-2 sm:grid-cols-3">
              <SummaryBadge label="Additions" value={s.summary.additions} />
              <SummaryBadge label="Shortages" value={s.summary.shortages} />
              <SummaryBadge label="Unchanged" value={s.summary.unchanged} />
            </div>
            <CountTable entries={entries} readonly />
          </Card>
        ) : (
          <fieldset disabled={save.isPending || apply.isPending || cancel.isPending} className="space-y-4">
            <Card
              title={`Rows (${countedCount} of ${total} counted)`}
              actions={
                <span className="text-sm text-slate-500">
                  {differences.additions.length} additions · {differences.shortages.length} shortages · {differences.uncounted.length} uncounted
                </span>
              }
            >
              {total === 0 ? (
                <Empty>This bin is empty. There is nothing to count; finish a full count to record it.</Empty>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data w-full text-sm">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Colour</th>
                        <th>Condition</th>
                        <th className="text-right">Expected</th>
                        <th className="text-right">Counted</th>
                        <th className="text-right">Difference</th>
                        <th>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((e, i) => {
                        const diff = e.counted === '' ? null : Number(e.counted) - e.expectedQty;
                        return (
                          <tr key={e.id ?? `new-${i}`}>
                            <td className="font-medium">{e.itemNo}</td>
                            <td>{e.colorName}</td>
                            <td>
                              {e.condition === 'N' ? 'New' : 'Used'}
                              {e.subCondition ? ` · ${e.subCondition}` : ''}
                            </td>
                            <td className="text-right tabular-nums">{number(e.expectedQty)}</td>
                            <td className="text-right">
                              <Input
                                aria-label={`Counted ${e.itemNo}`}
                                inputMode="numeric"
                                className="w-20 text-right"
                                value={e.counted}
                                onChange={(ev) => patch(i, { counted: ev.target.value })}
                              />
                            </td>
                            <td className="text-right tabular-nums">
                              {diff === null ? '—' : diff === 0 ? <span className="text-slate-400">0</span> : <span className={diff > 0 ? 'text-emerald-700' : 'text-red-700'}>{diff > 0 ? `+${diff}` : diff}</span>}
                            </td>
                            <td>
                              <Input aria-label={`Note ${e.itemNo}`} value={e.note} onChange={(ev) => patch(i, { note: ev.target.value })} placeholder={diff !== null && diff !== 0 ? 'Required' : ''} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {writable && (
              <Card title="Found something else">
                <div className="flex flex-wrap items-end gap-2">
                  <Field label="Type">
                    <Select value={found.itemType} onChange={(e) => setFound({ ...found, itemType: e.target.value })}>
                      <option>PART</option>
                      <option>SET</option>
                      <option>MINIFIG</option>
                      <option>GEAR</option>
                    </Select>
                  </Field>
                  <Field label="Item number">
                    <Input value={found.itemNo} onChange={(e) => setFound({ ...found, itemNo: e.target.value })} placeholder="3001" />
                  </Field>
                  <Field label="Colour">
                    <Select value={found.colorId} onChange={(e) => setFound({ ...found, colorId: Number(e.target.value) })}>
                      {colors.data?.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Condition">
                    <Select value={found.condition} onChange={(e) => setFound({ ...found, condition: e.target.value })}>
                      <option value="N">New</option>
                      <option value="U">Used</option>
                    </Select>
                  </Field>
                  <Field label="Completeness">
                    <Select value={found.subCondition} onChange={(e) => setFound({ ...found, subCondition: e.target.value })}>
                      <option value="">(Not applicable)</option>
                      <option value="C">Complete</option>
                      <option value="I">Incomplete</option>
                      <option value="S">Sealed</option>
                    </Select>
                  </Field>
                  <Button variant="secondary" disabled={!found.itemNo.trim()} onClick={addFound}>
                    Add to count
                  </Button>
                </div>
                <p className="mt-2 text-xs text-slate-500">Only items already in your catalogue can be added. Creating a catalogue record is a separate step.</p>
              </Card>
            )}

            <Card title="Session note">
              <Input value={note} onChange={(e) => { setNote(e.target.value); setDirty(true); }} placeholder="Optional note for the whole count" />
            </Card>

            {writable && (
              <Card title="Finish">
                <div className="flex flex-wrap items-center gap-3">
                  <Button disabled={!finishState.full} onClick={() => finish('full')}>
                    Finish full count
                  </Button>
                  <Button variant="secondary" disabled={!finishState.partial} onClick={() => finish('partial')}>
                    Finish partial count
                  </Button>
                  <span className="text-sm text-slate-500">
                    {finishState.message}
                  </span>
                </div>
                <div className="mt-3 border-t border-slate-100 pt-3">
                  <Button variant="ghost" disabled={cancel.isPending} onClick={() => cancel.mutate()}>
                    Cancel this count
                  </Button>
                </div>
              </Card>
            )}
          </fieldset>
        )}
      </div>
    </>
  );
}

function SummaryBadge({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-slate-200 px-3 py-2">
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div className="text-lg font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function CountTable({ entries, readonly }: { entries: Entry[]; readonly: boolean }) {
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="data w-full text-sm">
        <thead>
          <tr>
            <th>Item</th>
            <th>Colour</th>
            <th>Condition</th>
            <th className="text-right">Expected</th>
            <th className="text-right">Counted</th>
            <th className="text-right">Difference</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e, i) => {
            const diff = e.counted === '' ? null : Number(e.counted) - e.expectedQty;
            return (
              <tr key={e.id ?? `new-${i}`}>
                <td className="font-medium">{e.itemNo}</td>
                <td>{e.colorName}</td>
                <td>
                  {e.condition === 'N' ? 'New' : 'Used'}
                  {e.subCondition ? ` · ${e.subCondition}` : ''}
                </td>
                <td className="text-right tabular-nums">{number(e.expectedQty)}</td>
                <td className="text-right tabular-nums">{e.counted === '' ? '—' : number(Number(e.counted))}</td>
                <td className="text-right tabular-nums">{diff === null ? '—' : diff}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


/** Printer-friendly blind count sheet. It intentionally never renders expected or counted values. */
export function StocktakeSheet() {
  const { id } = useParams();
  const session = useStocktakeSession(Number(id));
  if (session.isPending) return <Spinner />;
  if (session.error || !session.data) return <ErrorMessage error={session.error} />;
  const s = session.data;
  const rows = stocktakePrintRows(s.rows);
  return (
    <main data-document className="mx-auto max-w-4xl bg-white p-8 print:max-w-none print:p-4">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">Stocktake Sheet</h1>
          <p className="text-slate-600">Bin: {s.capturedLabel}</p>
          <p className="text-sm text-slate-500">Write the quantity you physically count. No expected quantities are shown.</p>
        </div>
        <PrintDocumentButton recordType="stocktake" recordId={s.id} kind="sheet" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {rows.map((row) => (
          <article key={row.id} className="break-inside-avoid rounded border-2 border-slate-300 p-4">
            <div className="flex gap-3">
              <img className="size-20 object-contain" src={`https://img.bricklink.com/ItemImage/${row.itemType.toUpperCase()}/${row.itemNo}.png`} alt="" />
              <div>
                <div className="font-semibold">{row.itemNo}</div>
                <div className="text-sm">{row.itemName}</div>
                <div className="mt-1 text-sm text-slate-600">
                  {row.colorName} · {row.condition === 'N' ? 'New' : 'Used'}{row.subCondition ? ` · ${row.subCondition}` : ''}
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-end justify-between">
              <span className="text-sm font-medium">Counted quantity</span>
              <div aria-label={`Blank quantity box for ${row.itemNo}`} className="h-12 w-28 border-2 border-slate-800" />
            </div>
          </article>
        ))}
      </div>
    </main>
  );
}
